/**
 * The only failure codes `file_upload_failed` may carry (#7485). A code is an
 * exact, complete token from this set or `UNKNOWN`. It is never extracted from
 * an error by pattern or prefix: an error message can hold a file name, a
 * path, a URL segment or a token (`WORKSPACE_ACME_PAYROLL_2026.xlsx`), and any
 * of them that merely starts like one of ours must not leave the browser.
 *
 * Add a code here only when the upload client or an upload server function
 * really throws it. A code missing from the set is reported as `UNKNOWN`,
 * and the failure class, stage and status still say what happened.
 */
export const REPORTABLE_UPLOAD_FAILURE_CODES: ReadonlySet<string> = new Set([
  // The upload client.
  'COMPOSER_UPLOAD_TOO_LARGE',
  'UPLOAD_ABORTED',
  'UPLOAD_CONTENT_UNVERIFIED',
  'UPLOAD_FAILED',
  'UPLOAD_HASH_FAILED',
  'UPLOAD_HASH_WORKER_UNAVAILABLE',
  'UPLOAD_NOT_DURABLE',
  'UPLOAD_ORGANIZATION_UNAVAILABLE',
  'UPLOAD_PART_FAILED',
  'UPLOAD_REGISTRATION_INCOMPLETE',
  'UPLOAD_SESSION_FAILED',
  // Browser upload sessions (convex/media/browser_upload*).
  'BROWSER_UPLOAD_ABORT_NOT_OWNER',
  'BROWSER_UPLOAD_ABORT_STALE',
  'BROWSER_UPLOAD_ALREADY_BEGUN',
  'BROWSER_UPLOAD_BEGIN_IN_PROGRESS',
  'BROWSER_UPLOAD_BEGIN_STALE',
  'BROWSER_UPLOAD_COMPLETION_STALE',
  'BROWSER_UPLOAD_CONTENT_MISMATCH',
  'BROWSER_UPLOAD_IDENTITY_INVALID',
  'BROWSER_UPLOAD_NOT_UPLOADABLE',
  'BROWSER_UPLOAD_OBJECT_MISSING',
  'BROWSER_UPLOAD_PART_INVALID',
  'BROWSER_UPLOAD_PARTS_INVALID',
  'BROWSER_UPLOAD_RECEIPT_MISSING',
  'BROWSER_UPLOAD_RECOVERY_STATE_INCOMPLETE',
  'BROWSER_UPLOAD_SESSION_DENIED',
  'BROWSER_UPLOAD_SESSION_EXPIRED',
  'BROWSER_UPLOAD_SESSION_INVALID',
  'BROWSER_UPLOAD_STATE_INCOMPLETE',
  'BROWSER_UPLOAD_STORAGE_KEY_MISMATCH',
  'BROWSER_UPLOAD_TRANSPORT_MISMATCH',
  // Workspace quota and registration of the stored file.
  'WORKSPACE_STORAGE_LIMIT_EXCEEDED',
  'WORKSPACE_TEAM_STORAGE_LIMIT_EXCEEDED',
  'WORKSPACE_USER_STORAGE_LIMIT_EXCEEDED',
  'WORKSPACE_UPLOAD_ACTIVE_CONTENT',
  'WORKSPACE_UPLOAD_ALREADY_MATERIALIZED',
  'WORKSPACE_UPLOAD_HASH_VERIFICATION_INVALID',
  'WORKSPACE_UPLOAD_NOT_OWNED',
  'WORKSPACE_UPLOAD_ORG_MISMATCH',
  'WORKSPACE_UPLOAD_REPLACE_CONFLICT',
  'WORKSPACE_UPLOAD_SIZE_INVALID',
  'WORKSPACE_UPLOAD_UNSUPPORTED_TYPE',
  'WORKSPACE_UPLOADS_BUSY',
  'WORKSPACE_IMAGE_UPLOAD_BINDING_MISMATCH',
  'WORKSPACE_IMAGE_UPLOAD_CONTENT_MISMATCH',
  'WORKSPACE_IMAGE_UPLOAD_NOT_CLAIMED',
  'WORKSPACE_IMAGE_UPLOAD_OWNER_MISMATCH',
  'WORKSPACE_IMAGE_UPLOAD_RECEIPT_INVALID',
  'WORKSPACE_IMAGE_UPLOAD_RECEIPT_MISSING',
  'WORKSPACE_IMAGE_UPLOAD_REPLAY_CONFLICT',
  'WORKSPACE_IMAGE_UPLOAD_SOURCE_CHANGED',
  'WORKSPACE_IMAGE_UPLOAD_SOURCE_INVALID',
  'WORKSPACE_IMAGE_UPLOAD_STORAGE_CONFLICT',
  'WORKSPACE_FILE_CONTENT_HASH_CONFLICT',
  'WORKSPACE_FILE_WRITE_CONFLICT',
  'WORKSPACE_MEMBERSHIP_REQUIRED',
  'WORKSPACE_NOT_ACCESSIBLE',
  'WORKSPACE_PATH_ORG_MISMATCH',
  'WORKSPACE_TARGET_NOT_DIRECTORY',
]);

const TOKEN = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g;

/**
 * The first complete reportable token in `text`, else `UNKNOWN`. A longer
 * token that merely starts with a reportable one (`WORKSPACE_UPLOAD_NOT_OWNED_X`)
 * is one other token, not a match.
 */
export function reportableUploadFailureCode(text: string): string {
  for (const token of text.matchAll(TOKEN))
    if (REPORTABLE_UPLOAD_FAILURE_CODES.has(token[0])) return token[0];
  return 'UNKNOWN';
}
